class AveyAnnouncement extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') {
      return;
    }

    this.items = [...this.querySelectorAll('[data-announcement-item]')];
    this.indicators = [...this.querySelectorAll('[data-announcement-indicator]')];
    this.indicatorContainer = this.querySelector('[data-announcement-indicators]');
    this.dataset.initialized = 'true';
    this.headerSection = this.closest('.shopify-section');
    this.headerOverlay = this.headerSection?.querySelector('.avey-overlay');
    this.announcementHeight = Math.max(24, this.getBoundingClientRect().height);
    this.resizeObserver = new ResizeObserver((entries) => {
      const announcementEntry = entries.find((entry) => entry.target === this);
      const height = Math.max(24, announcementEntry?.contentRect.height || this.announcementHeight);
      if (height !== this.announcementHeight) {
        this.announcementHeight = height;
        this.syncCollapseDistance();
        this.scheduleVisibility();
      }
    });
    this.resizeObserver.observe(this);
    this.desktopMedia = window.matchMedia('(min-width: 1024px)');
    this.scrollTimelineSupported = CSS.supports('animation-timeline: scroll()');
    this.lastScrollY = window.scrollY;
    this.scrollFrame = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.touchStart = null;
    this.suppressClick = false;
    this.clickResetTimer = null;
    this.syncCollapseDistance();

    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.desktopMedia.addEventListener('change', this.onViewportChange);
    this.updateIndicatorInteractivity();

    if (this.items.length < 2) {
      this.updateVisibility();
      return;
    }

    this.index = Math.max(0, this.items.findIndex((item) => item.classList.contains('is-active')));
    this.interval = Math.max(3000, Number.parseInt(this.dataset.interval, 10) || 5000);

    this.addEventListener('pointerenter', this.pause);
    this.addEventListener('pointerleave', this.resume);
    this.addEventListener('focusin', this.pause);
    this.addEventListener('focusout', this.resume);
    this.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.addEventListener('touchend', this.onTouchEnd, { passive: true });
    this.addEventListener('touchcancel', this.onTouchCancel, { passive: true });
    this.addEventListener('click', this.onClickCapture, true);
    this.indicatorContainer?.addEventListener('click', this.onIndicatorClick);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.show(this.index);
    this.resume();
    this.updateVisibility();
  }

  disconnectedCallback() {
    this.pause();
    window.removeEventListener('scroll', this.onScroll);
    this.desktopMedia?.removeEventListener('change', this.onViewportChange);
    this.resizeObserver?.disconnect();
    this.removeEventListener('pointerenter', this.pause);
    this.removeEventListener('pointerleave', this.resume);
    this.removeEventListener('focusin', this.pause);
    this.removeEventListener('focusout', this.resume);
    this.removeEventListener('touchstart', this.onTouchStart);
    this.removeEventListener('touchend', this.onTouchEnd);
    this.removeEventListener('touchcancel', this.onTouchCancel);
    this.removeEventListener('click', this.onClickCapture, true);
    this.indicatorContainer?.removeEventListener('click', this.onIndicatorClick);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    if (this.clickResetTimer) window.clearTimeout(this.clickResetTimer);
    document.documentElement.style.removeProperty('--avey-announcement-progress');
    this.headerSection?.style.removeProperty('--avey-announcement-collapse-distance');
    this.setHidden(false);
  }

  scheduleVisibility = () => {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.updateVisibility();
    });
  };

  onScroll = () => {
    if (this.desktopMedia.matches) {
      this.scheduleVisibility();
      return;
    }

    this.updateVisibility();
  };

  updateVisibility() {
    const currentScrollY = window.scrollY;
    const desktop = this.desktopMedia.matches;

    if (!desktop) {
      const collapseDistance = this.getCollapseDistance(180, 30);
      if (this.scrollTimelineSupported && !this.reducedMotion.matches) {
        this.setHidden(currentScrollY >= collapseDistance);
        this.lastScrollY = currentScrollY;
        return;
      }

      if (this.reducedMotion.matches) {
        this.setHidden(currentScrollY >= collapseDistance);
        this.lastScrollY = currentScrollY;
        return;
      }

      const previousScrollY = this.lastScrollY ?? currentScrollY;
      const currentlyHidden = this.headerSection?.classList.contains(
        'avey-header-section--announcement-hidden',
      ) ?? false;
      let hidden = currentlyHidden;

      if (currentScrollY <= 80) hidden = false;
      else if (currentScrollY >= 120 && currentScrollY > previousScrollY) hidden = true;
      else if (currentScrollY < previousScrollY) hidden = false;

      this.lastScrollY = currentScrollY;
      this.setHidden(hidden);
      return;
    }

    this.updateProgress(currentScrollY, 240, 32);
  }

  updateProgress(scrollY, referenceDistance, referenceHeight) {
    this.lastScrollY = scrollY;
    const collapseDistance = this.getCollapseDistance(referenceDistance, referenceHeight);
    const rawProgress = Math.min(1, Math.max(0, scrollY / collapseDistance));
    const progress = this.reducedMotion.matches ? Number(rawProgress >= 1) : rawProgress;

    document.documentElement.style.setProperty('--avey-announcement-progress', progress.toFixed(4));
    this.setHidden(progress >= 1);
  }

  getCollapseDistance(referenceDistance, referenceHeight) {
    return referenceDistance * Math.max(1, this.announcementHeight / referenceHeight);
  }

  syncCollapseDistance() {
    if (!this.desktopMedia || !this.headerSection) return;

    if (!this.desktopMedia.matches) {
      this.headerSection.style.setProperty(
        '--avey-announcement-collapse-distance',
        `${this.getCollapseDistance(180, 30)}px`,
      );
    } else {
      this.headerSection.style.removeProperty('--avey-announcement-collapse-distance');
    }
  }

  setHidden(hidden) {
    const wasHidden = this.headerSection?.classList.contains(
      'avey-header-section--announcement-hidden',
    ) ?? false;
    const rootWasHidden = document.documentElement.hasAttribute('data-avey-announcement-hidden');
    if (wasHidden === hidden && rootWasHidden === hidden) return;

    this.headerSection?.classList.toggle('avey-header-section--announcement-hidden', hidden);
    document.documentElement.toggleAttribute('data-avey-announcement-hidden', hidden);
    this.syncOverlayOffset(hidden);
  }

  syncOverlayOffset(hidden) {
    if (!this.headerOverlay) return;

    this.headerOverlay.style.setProperty('--avey-overlay-offset-top', hidden ? '60px' : 'var(--avey-header-height)');
  }

  updateIndicatorInteractivity() {
    if (!this.indicatorContainer) return;

    const isDesktop = this.desktopMedia.matches;
    if (isDesktop) this.indicatorContainer.removeAttribute('aria-hidden');
    else this.indicatorContainer.setAttribute('aria-hidden', 'true');

    this.indicators.forEach((indicator) => {
      if (isDesktop) indicator.removeAttribute('tabindex');
      else indicator.setAttribute('tabindex', '-1');
    });
  }

  show(index) {
    this.items.forEach((item, itemIndex) => {
      const active = itemIndex === index;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-hidden', String(!active));
      item.querySelectorAll('a, button').forEach((control) => {
        if (active) control.removeAttribute('tabindex');
        else control.setAttribute('tabindex', '-1');
      });
    });
    this.indicators.forEach((indicator, indicatorIndex) => {
      const active = indicatorIndex === index;
      indicator.classList.toggle('is-active', active);
      indicator.setAttribute('aria-pressed', String(active));
    });
    this.index = index;
  }

  next = () => {
    this.show((this.index + 1) % this.items.length);
  };

  previous = () => {
    this.show((this.index - 1 + this.items.length) % this.items.length);
  };

  onTouchStart = (event) => {
    const touch = event.touches[0];
    if (!touch) return;

    this.touchStart = { x: touch.clientX, y: touch.clientY };
    this.pause();
  };

  onTouchEnd = (event) => {
    const touch = event.changedTouches[0];
    if (!touch || !this.touchStart) {
      this.onTouchCancel();
      return;
    }

    const deltaX = touch.clientX - this.touchStart.x;
    const deltaY = touch.clientY - this.touchStart.y;
    const isHorizontalSwipe = Math.abs(deltaX) >= 40 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2;

    this.touchStart = null;
    if (isHorizontalSwipe) {
      this.suppressClick = true;
      if (deltaX < 0) this.next();
      else this.previous();
      if (this.clickResetTimer) window.clearTimeout(this.clickResetTimer);
      this.clickResetTimer = window.setTimeout(() => {
        this.suppressClick = false;
        this.clickResetTimer = null;
      }, 400);
    }
    this.resume();
  };

  onTouchCancel = () => {
    this.touchStart = null;
    this.resume();
  };

  onClickCapture = (event) => {
    if (!this.suppressClick) return;
    event.preventDefault();
    event.stopPropagation();
  };

  onIndicatorClick = (event) => {
    if (!this.desktopMedia.matches) return;

    const indicator = event.target.closest('[data-announcement-indicator]');
    const index = this.indicators.indexOf(indicator);
    if (index < 0) return;

    this.show(index);
  };

  pause = () => {
    window.clearInterval(this.timer);
    this.timer = null;
  };

  resume = () => {
    this.pause();
    if (document.hidden || this.reducedMotion.matches) return;
    this.timer = window.setInterval(this.next, this.interval);
  };

  onVisibilityChange = () => {
    if (document.hidden) this.pause();
    else this.resume();
  };

  onViewportChange = () => {
    if (!this.desktopMedia.matches) {
      document.documentElement.style.setProperty('--avey-announcement-progress', '0');
    }
    this.syncCollapseDistance();
    this.updateIndicatorInteractivity();
    this.updateVisibility();
  };
}

if (!customElements.get('avey-announcement')) {
  customElements.define('avey-announcement', AveyAnnouncement);
}
